import { translate, useLocale } from "../localization";
import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import ExperiencesApi from "../services/ExperiencesAPI";

const ExperiencesContext = createContext();

export const useExperiences = () => {
  useLocale();
  return useContext(ExperiencesContext);
};

export const ExperiencesProvider = ({ children }) => {
  useLocale();
  const { organizationId } = useAuth();

  const [experiences, setExperiences] = useState(null);
  const [guides, setGuides] = useState(null);
  const [insights, setInsights] = useState(null);
  const [assessments, setAssessments] = useState(null);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);

  useEffect(() => {
    if (organizationId) {
      fetchData();
    } else {
      console.log("No organization found");
    }
  }, [organizationId]);

  // When experiences are loaded, fetch assessments for each experience
  useEffect(() => {
    if (experiences && experiences.length > 0 && assessments === null && !assessmentsLoading) {
      fetchAllAssessments(experiences);
    }
  }, [experiences, assessments, assessmentsLoading]);

  const fetchExperiences = () => {
    if (!organizationId) {
      setExperiences([]);
      return Promise.resolve([]);
    }

    return ExperiencesApi.get(`experiences?organization=${organizationId}`, { requiresAuth: true })
      .then((res) => {
        console.log("Experiences fetched:", res?.data);
        setExperiences(res?.data || []);
      })
      .catch((err) => console.error("Error fetching experiences:", err));
  };

  const fetchGuides = () => {
    if (!organizationId) {
      setGuides([]);
      return Promise.resolve([]);
    }

    return ExperiencesApi.get(`guides?organization=${organizationId}`, { requiresAuth: true })
      .then((res) => setGuides(res?.data || []))
      .catch((err) => console.error("Error fetching guides:", err));
  };

  const fetchInsights = () =>
    ExperiencesApi.get(`insights`, { requiresAuth: true })
      .then((res) => {
        console.log("Insights fetched:", res?.data);
        setInsights(res?.data || []);
      })
      .catch((err) => console.error("Error fetching insights:", err));

  /**
   * Fetch ALL assessments for all experiences.
   * 
   * The API structure is:
   * - Assessments are stored with pk: ASSESSMENT#EXPERIENCE#${experienceId}
   * - And sk: INSIGHT#${insightId}#ASSESSMENT#${assessmentId}
   * 
   * Since GET /assessments doesn't pass experienceId, we need to query
   * assessments for each experience individually.
   */
  // const fetchAllAssessments = async (experiencesList) => {
  //   if (!experiencesList || experiencesList.length === 0) {
  //     console.log("No experiences to fetch assessments for");
  //     setAssessments([]);
  //     return;
  //   }

  //   setAssessmentsLoading(true);
  //   console.log("Fetching assessments for", experiencesList.length, "experiences...");

  //   try {
  //     const allAssessments = [];

  //     // Get experience IDs - check various possible field names
  //     const experienceIds = experiencesList
  //       .map((exp) => {
  //         // Log structure of first experience to understand the data
  //         if (experiencesList.indexOf(exp) === 0) {
  //           console.log("Experience structure:", exp);
  //           console.log("Experience keys:", Object.keys(exp));
  //         }
  //         return exp.experienceId || exp.id || exp.sk?.split("EXPERIENCE#")[1];
  //       })
  //       .filter(Boolean);

  //     console.log("Experience IDs found:", experienceIds);

  //     // Fetch assessments for each experience in parallel
  //     const results = await Promise.all(
  //       experienceIds.map(async (expId) => {
  //         try {
  //           const expResponse = await ExperiencesApi.get(`experiences/${expId}`, { 
  //             requiresAuth: false 
  //           });
            
  //           // console.log(`Experience ${expId} full data:`, expResponse?.data);
            
  //           const expData = expResponse?.data;
            
  //           if (expData) {
  //             // Check if there are embedded assessments
  //             if (expData.assessments) {
  //               return expData.assessments.map(a => ({ ...a, experienceId: expId }));
  //             }
              
  //             // Check if the response has items that are assessments
  //             if (Array.isArray(expData)) {
  //               return expData
  //                 .filter(item => item.pk?.includes("ASSESSMENT") || item.assessmentId)
  //                 .map(a => ({ ...a, experienceId: expId }));
  //             }
              
  //             return [];
  //           }
  //           return [];
  //         } catch (err) {
  //           console.warn(`Error fetching for experience ${expId}:`, err.message);
  //           return [];
  //         }
  //       })
  //     );

  //     // Flatten results
  //     results.forEach((arr) => {
  //       if (Array.isArray(arr)) {
  //         allAssessments.push(...arr);
  //       }
  //     });

  //     console.log("Total assessments collected:", allAssessments.length);
      
  //     // If we still have no assessments, check if experiences contain them
  //     if (allAssessments.length === 0) {
  //       console.log("No assessments found via experience fetch. Checking experience data directly...");
        
  //       experiencesList.forEach(exp => {
  //         if (exp.assessments && Array.isArray(exp.assessments)) {
  //           exp.assessments.forEach(a => {
  //             allAssessments.push({ 
  //               ...a, 
  //               experienceId: exp.experienceId || exp.id 
  //             });
  //           });
  //         }
  //       });
        
  //       console.log("Assessments from experience data:", allAssessments.length);
  //     }

  //     setAssessments(allAssessments);
  //   } catch (err) {
  //     console.error("Error in fetchAllAssessments:", err);
  //     setAssessments([]);
  //   } finally {
  //     setAssessmentsLoading(false);
  //   }
  // };

  const fetchAllAssessments = async (experiencesList) => {
    if (!experiencesList || experiencesList.length === 0) {
      console.log("No experiences to fetch assessments for");
      setAssessments([]);
      return;
    }

    setAssessmentsLoading(true);

    const experienceIds = experiencesList
      .map((exp) => exp.experienceId)
      .filter(Boolean);

    console.log(`🔄 Fetching assessments for ${experienceIds.length} experiences...`);

    try {
      const allAssessments = [];
      const BATCH_SIZE = 10;

      for (let i = 0; i < experienceIds.length; i += BATCH_SIZE) {
        const batch = experienceIds.slice(i, i + BATCH_SIZE);

        const batchResults = await Promise.all(
          batch.map(async (expId) => {
            try {
              const res = await ExperiencesApi.get(`assessments?experienceId=${expId}`, {
                requiresAuth: true,
              });

              const data = res?.data;

              if (Array.isArray(data) && data.length > 0) {
                return data.map((a) => ({ ...a, experienceId: expId }));
              }
              return [];
            } catch (err) {
              return [];
            }
          })
        );

        batchResults.forEach((arr) => {
          if (Array.isArray(arr)) {
            allAssessments.push(...arr);
          }
        });
      }

      console.log(`✅ Total assessments loaded: ${allAssessments.length}`);
      setAssessments(allAssessments);
    } catch (err) {
      console.error("❌ Error in fetchAllAssessments:", err);
      setAssessments([]);
    } finally {
      setAssessmentsLoading(false);
    }
  };
  
  const fetchData = () => {
    console.log("Fetching experiences data for organization:", organizationId);
    fetchExperiences();
    fetchGuides();
    fetchInsights();
  };

  const postGuide = async (guidePayload) => {
    if (!guidePayload?.id) throw new Error(translate("postGuide requires a guide with an id"));

    try {
      await ExperiencesApi.post(
        "guides",
        { ...guidePayload, organizationId },
        { requiresAuth: true }
      );
    } catch (err) {
      console.error("Error posting guide:", err);
      throw err;
    }
  };

  const extractGuideId = (g) => (g?.sk || "").split("GUIDE#")[1];

  const deleteGuide = async (guideId) => {
    if (!guideId) return;

    setGuides((prev) =>
      Array.isArray(prev) ? prev.filter((g) => extractGuideId(g) !== guideId) : prev
    );

    try {
      await ExperiencesApi.get(`guides/${guideId}/delete`, { requiresAuth: true });
      await fetchGuides();
    } catch (err) {
      console.error("Delete failed, rolling back via refetch:", err);
      await fetchGuides();
      throw err;
    }
  };

  const value = {
    experiences,
    guides,
    insights,
    assessments,
    assessmentsLoading,
    fetchData,
    fetchGuides,
    deleteGuide,
    postGuide,
  };

  return (
    <ExperiencesContext.Provider value={value}>
      {children}
    </ExperiencesContext.Provider>
  );
};

